import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { products } from "../data/products";
import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/formatters";
import Button from "../components/ui/Button";
import {
  StarSolidIcon,
  ShoppingBagIcon,
  HeartIcon,
  ChevronLeftIcon,
} from "../utils/icons";
import { useState } from "react";
import { Link } from "react-router-dom";

export default function ProductDetails() {
  const [selectedImage, setSelectedImage] = useState(0);

  const { id } = useParams();
  const { addToCart } = useCart();
  const product = products.find((p) => p.id === parseInt(id) || p.id === id);

  // Get related products (products from same category, excluding current product)
  const relatedProducts = products
    .filter((p) => p.category === product.category && p.id !== id)
    .slice(0, 4);

  // Product images array with actual stock images
  const getProductImages = (product) => {
    // If product already has images array, return it
    if (product.images && Array.isArray(product.images)) {
      return product.images;
    }

    // If product has a single image, create an array
    const mainImage = product.image;
    return [mainImage, mainImage, mainImage, mainImage];
  };

  const productImages = getProductImages(product);

  if (!product) {
    return <div>Product not found</div>;
  }

  const handleAddToCart = () => {
    addToCart(product);
  };

  return (
    <div className="bg-white pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="aspect-square rounded-2xl overflow-hidden bg-gray-100"
            >
              <img
                src={productImages[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover object-center"
              />
            </motion.div>

            {/* Thumbnail Gallery */}
            <div className="grid grid-cols-4 gap-4">
              {productImages.map((image, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square rounded-lg overflow-hidden bg-gray-100 ${
                    selectedImage === index ? "ring-2 ring-[#DB5461]" : ""
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.name} view ${index + 1}`}
                    className="w-full h-full object-cover object-center"
                  />
                </motion.button>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-serif font-bold text-gray-900">
                  {product.name}
                </h1>
                <div className="flex items-center">
                  <StarSolidIcon className="h-5 w-5 text-[#DB5461]" />
                  <span className="ml-1 text-sm text-gray-600">4.5</span>
                </div>
              </div>
              <p className="mt-2 text-xl font-medium text-[#DB5461]">
                {formatCurrency(product.price)}
              </p>
            </div>

            <p className="text-gray-600">{product.description}</p>

            <Link
              to="/products"
              className="text-[#DB5461] hover:text-[#DB5461]/80 flex items-center gap-2 mb-4"
            >
              <ChevronLeftIcon className="h-5 w-5" />
              Back to Products
            </Link>

            <div className="flex space-x-4">
              <Button
                onClick={handleAddToCart}
                className="w-full bg-[#DB5461] text-white hover:bg-[#DB5461]/90"
              >
                <ShoppingBagIcon className="h-5 w-5 mr-2" />
                Add to Cart
              </Button>
              <Button variant="outline" size="lg">
                <HeartIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Product Information Tabs */}
            <div className="border-t border-gray-200 pt-8 space-y-8">
              {product.category === "Bundles" ? (
                <>
                  {/* Bundle Contents */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      What's Included
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {product.details.includes.map((item, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center space-x-2"
                        >
                          <svg
                            className="h-5 w-5 text-[#DB5461] flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-gray-600">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Usage Instructions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      How to Use
                    </h3>
                    <p className="text-gray-600">{product.details.usage}</p>
                  </div>

                  {/* Benefits */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Key Benefits
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {product.details.benefits.map((benefit, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center space-x-2"
                        >
                          <svg
                            className="h-5 w-5 text-[#DB5461] flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-gray-600">{benefit}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Value Proposition */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Bundle Value
                    </h3>
                    <div className="bg-[#DB5461]/10 p-4 rounded-lg">
                      <p className="text-[#DB5461] font-medium">
                        {product.details.value}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Usage Instructions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      How to Use
                    </h3>
                    <p className="text-gray-600">{product.details.usage}</p>
                  </div>

                  {/* Benefits */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Key Benefits
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {product.details.benefits.map((benefit, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center space-x-2"
                        >
                          <svg
                            className="h-5 w-5 text-[#DB5461] flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-gray-600">{benefit}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Ingredients */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Ingredients
                    </h3>
                    <p className="text-gray-600">
                      {product.details.ingredients}
                    </p>
                  </div>

                  {/* When to Use */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      When to Use
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {product.details.whenToUse.map((item, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center space-x-2"
                        >
                          <svg
                            className="h-5 w-5 text-[#DB5461] flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="text-gray-600">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Pro Tips */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Pro Tips
                    </h3>
                    <div className="bg-[#DB5461]/10 p-4 rounded-lg">
                      <p className="text-gray-600">{product.details.tips}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>

        {/* Related Products Section */}
        <div className="mt-24">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-8">
            Recommended Pairings
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {relatedProducts.map((relatedProduct) => (
              <motion.div
                key={relatedProduct.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/products/${relatedProduct.id}`}
                  className="group block"
                >
                  <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900 group-hover:text-[#DB5461] transition-colors duration-300">
                    {relatedProduct.name}
                  </h3>
                  <p className="mt-1 text-gray-500">
                    {formatCurrency(relatedProduct.price)}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
